import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import {
  comparisonCategories,
  compactRows,
  type CellValue,
  type ComparisonRow,
} from '../data/comparisonData'

interface Props {
  compact?: boolean
}

function Cell({ value, highlight, isAspired }: { value: CellValue; highlight?: boolean; isAspired?: boolean }) {
  if (value.type === 'text') {
    return (
      <span className={`comparison-text${highlight ? ' comparison-text-highlight' : ''}${isAspired ? ' comparison-text-aspired' : ''}`}>
        {value.value}
      </span>
    )
  }
  if (value.type === 'check') {
    return (
      <span className="comparison-mark-wrap">
        <span className="comparison-mark comparison-mark-check" aria-label="Yes">✓</span>
        {value.label && <span className="comparison-mark-label">{value.label}</span>}
      </span>
    )
  }
  if (value.type === 'cross') {
    return (
      <span className="comparison-mark-wrap">
        <span className="comparison-mark comparison-mark-cross" aria-label="No">✕</span>
        {value.label && <span className="comparison-mark-label">{value.label}</span>}
      </span>
    )
  }
  return (
    <span className="comparison-mark-wrap">
      <span className="comparison-mark comparison-mark-partial" aria-label="Partial">~</span>
      <span className="comparison-mark-label">{value.label}</span>
    </span>
  )
}

function Row({ row }: { row: ComparisonRow }) {
  const rowClass = row.highlight ? 'comparison-row comparison-highlight-row' : 'comparison-row'
  return (
    <tr className={rowClass}>
      <td className="comparison-feature">{row.feature}</td>
      <td className="comparison-cell comparison-aspired-cell">
        <Cell value={row.aspired} highlight={row.highlight} isAspired />
      </td>
      <td className="comparison-cell">
        <Cell value={row.sintra} highlight={row.highlight} />
      </td>
      <td className="comparison-cell">
        <Cell value={row.chatgpt} highlight={row.highlight} />
      </td>
    </tr>
  )
}

function Header() {
  return (
    <thead>
      <tr className="comparison-header">
        <th className="comparison-feature-head" scope="col">Feature</th>
        <th className="comparison-aspired-head" scope="col">
          <span className="comparison-head-brand">Aspired AI</span>
        </th>
        <th scope="col">Sintra AI</th>
        <th scope="col">ChatGPT</th>
      </tr>
    </thead>
  )
}

export default function ComparisonTable({ compact = false }: Props) {
  if (compact) {
    return (
      <div className="comparison-wrap">
        <div className="comparison-scroll">
          <table className="comparison-table">
            <Header />
            <tbody>
              {compactRows.map((row) => (
                <Row key={row.feature} row={row} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="comparison-see-full">
          <Link to="/compare">See the full feature-by-feature breakdown →</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="comparison-wrap">
      <div className="comparison-scroll">
        <table className="comparison-table">
          <Header />
          <tbody>
            {comparisonCategories.map((category) => (
              <Fragment key={category.label}>
                <tr className="comparison-category-row">
                  <td colSpan={4} className="comparison-category-label">{category.label}</td>
                </tr>
                {category.rows.map((row) => (
                  <Row key={`${category.label}-${row.feature}`} row={row} />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
